'use client';

import { useState } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { CopilotPlanMessage } from '@terra/shared-dto-chat-types';
import { Button } from '../../../components/styled/button';
import { FlatToolCardActionButton } from './tools/shared/flat-tool-card-action-button';
import {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
} from './trace';

export type PlanViewerData = {
  filePath: string;
  title?: string;
};

export type OnOpenPlanInMainContent = (data: PlanViewerData) => void;

export function CopilotPlanCard({
  dataMessage,
  onOpenPlanInMainContent,
  onBuildPlan,
}: {
  dataMessage?: CopilotPlanMessage;
  onOpenPlanInMainContent?: OnOpenPlanInMainContent;
  onBuildPlan?: () => void;
}) {
  const t = useTranslations('designSystem.chat.copilotPlanCard');
  const [open, setOpen] = useState(false);

  if (!dataMessage) {
    return null;
  }

  const openPlan = () => {
    onOpenPlanInMainContent?.({
      filePath: dataMessage.filePath,
      title: dataMessage.title,
    });
  };

  const steps = [...dataMessage.steps].sort(
    (left, right) => left.order - right.order,
  );
  const canExpand = steps.length > 0;
  const title = dataMessage.title.trim();
  const line = (
    <TraceLine
      title={title || t('heading')}
      description={dataMessage.summary}
      canExpand={canExpand}
      isExpanded={open}
    />
  );

  return (
    <VStack alignItems="stretch" gap={1} w="full" minW={0}>
      {canExpand ? (
        <TraceCollapsible
          open={open}
          onOpenChange={({ open: nextOpen }) => setOpen(nextOpen)}
        >
          <TraceCollapsibleTrigger>{line}</TraceCollapsibleTrigger>
          <TraceCollapsibleContent open={open}>
            <Trace nested>
              {steps.map((step) => (
                <TraceLine
                  key={`${step.order}-${step.title}`}
                  title={step.title}
                  description={step.detail}
                />
              ))}
            </Trace>
          </TraceCollapsibleContent>
        </TraceCollapsible>
      ) : (
        line
      )}
      {onOpenPlanInMainContent || onBuildPlan ? (
        <HStack gap={1.5} justifyContent="flex-start" alignItems="center">
          {onOpenPlanInMainContent ? (
            <FlatToolCardActionButton onClick={openPlan}>
              {t('viewPlan')}
            </FlatToolCardActionButton>
          ) : null}
          {onBuildPlan ? (
            <Button
              size="xs"
              variant="solid"
              type="button"
              h="6"
              minH="6"
              px={2.5}
              bg="fg.default"
              color="bg.default"
              _hover={{ bg: 'fg.muted' }}
              onClick={onBuildPlan}
            >
              {t('build')}
            </Button>
          ) : null}
        </HStack>
      ) : null}
    </VStack>
  );
}
